import { queryOptions, mutationOptions } from '@tanstack/react-query';
import type { Stored } from './stored';
import { apiClient } from './client';

import type { components } from './generated/hsOfficeApi';

type Schemas = components['schemas'];

export const partnerKeys = {
  all: ['partners'] as const,
  list: () => [...partnerKeys.all, 'list'] as const,
  /* deliberately NOT under `all`: the list stays unassumed and keeps
     its cache across partner switches, the single-partner queries are
     role-scoped and get invalidated on every switch */
  single: (uuid: string | undefined) => ['partner', uuid] as const,
};

export const partnerQueries = {
  /*
   * All partners visible to the logged-in subject (via grants);
   * most customers see exactly one. Always unassumed (see
   * assumedRoles.ts), so the embedded `details` stay empty here.
   */
  list: () =>
    queryOptions({
      queryKey: partnerKeys.list(),
      queryFn: async () => {
        const { data } =
          await apiClient.get<Stored<Schemas['HsOfficePartner']>[]>(
            '/office/partners'
          );
        return data;
      },
    }),

  /*
   * One partner with its role-dependent embedded `details` (filled
   * from the relation-AGENT level upwards) — this request carries the
   * assumed partner role, unlike the list above.
   */
  single: (uuid: string | undefined) =>
    queryOptions({
      queryKey: partnerKeys.single(uuid),
      queryFn: async () => {
        const { data } = await apiClient.get<
          Stored<Schemas['HsOfficePartner']>
        >(`/office/partners/${uuid}`);
        return data;
      },
    }),
};

/*
 * Writing a partner is reserved for global admins (see
 * `useGlobalAdmin`), and these requests deliberately go out without an
 * assumed role — the sidebar selection must not narrow what the writer
 * is allowed to do (see assumedRoles.ts).
 */
export const partnerMutations = {
  post: () =>
    mutationOptions({
      mutationFn: async (partnerInsert: Schemas['HsOfficePartnerInsert']) => {
        const { data } = await apiClient.post<
          Stored<Schemas['HsOfficePartner']>
        >('/office/partners', partnerInsert);
        return data;
      },
    }),
  patch: (uuid: string | undefined) =>
    mutationOptions({
      mutationFn: async (partnerPatch: Schemas['HsOfficePartnerPatch']) => {
        const { data } = await apiClient.patch<
          Stored<Schemas['HsOfficePartner']>
        >(`/office/partners/${uuid}`, partnerPatch);
        return data;
      },
    }),
  delete: () =>
    mutationOptions({
      /* the uuid comes per call: the delete button belongs to a row,
         not to a page holding one partner */
      mutationFn: async (uuid: string) => {
        await apiClient.delete(`/office/partners/${uuid}`);
      },
    }),
};
